import { computed, ref, watch, type Ref } from 'vue';

type ItemState = 'done' | 'dismissed';

// localStorage-backed state for revision plan action items, keyed by project +
// framework. Items are identified by a hash of their text, so state survives
// reordering but resets if an item is reworded (by design). Each item is
// either done (checked off) or dismissed (kept visible but muted and excluded
// from progress). Progress is computed against the currently parsed items
// only, so stale hashes from a regenerated plan never inflate the count.
export function useRevisionProgress(projectId: Ref<string>, frameworkId: Ref<string>, currentActionIds: Ref<string[]>) {
  const storageKey = computed(() => `revision_progress_${projectId.value}_${frameworkId.value}`);
  const states = ref<Record<string, ItemState>>({});

  watch(
    storageKey,
    key => {
      try {
        const stored = JSON.parse(localStorage.getItem(key) ?? '{}');
        if (Array.isArray(stored)) {
          // Earlier format: a plain array of checked ids
          states.value = Object.fromEntries(stored.map((id: string) => [id, 'done']));
        } else if (stored && typeof stored === 'object') {
          states.value = stored;
        } else {
          states.value = {};
        }
      } catch {
        states.value = {};
      }
    },
    { immediate: true }
  );

  function persist() {
    try {
      localStorage.setItem(storageKey.value, JSON.stringify(states.value));
    } catch {
      // Private browsing or full storage; state still works for the session
    }
  }

  function setState(id: string, state: ItemState | undefined) {
    const next = { ...states.value };
    if (state === undefined) {
      delete next[id];
    } else {
      next[id] = state;
    }
    states.value = next;
    persist();
  }

  const isChecked = (id: string) => states.value[id] === 'done';
  const isDismissed = (id: string) => states.value[id] === 'dismissed';

  function toggle(id: string) {
    setState(id, isChecked(id) ? undefined : 'done');
  }

  // Dismiss keeps the item visible but muted and out of the progress count;
  // calling it on a dismissed item restores it.
  function toggleDismissed(id: string) {
    setState(id, isDismissed(id) ? undefined : 'dismissed');
  }

  const dismissedCount = computed(() => currentActionIds.value.filter(id => isDismissed(id)).length);
  const completedCount = computed(() => currentActionIds.value.filter(id => isChecked(id)).length);
  const totalCount = computed(() => currentActionIds.value.length - dismissedCount.value);

  return { isChecked, isDismissed, toggle, toggleDismissed, completedCount, totalCount, dismissedCount };
}
